import { useState } from "react";
import {
  useDeleteCityMutation,
  useFetchCitiesQuery,
  useToggleCityIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination.tsx";
import ToggleSwitch from "../components/common/ToggleSwitch.tsx";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn.tsx";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import TextField from "../components/common/TextField.tsx";
import useDebounce from "../hooks/useDebounce.tsx";
import { Link } from "react-router-dom";
import { City } from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";
import { tdClasses, thClasses } from "../configs";

const Cities: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cityToDelete, setCityToDelete] = useState<City | null>(null);

  const [deleteCity] = useDeleteCityMutation();
  const [toggleActive] = useToggleCityIsActiveStatusMutation();

  const confirmDelete = (cityToDelete: City) => {
    setCityToDelete(cityToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteCity = async () => {
    if (!cityToDelete) return;
    const result = await deleteCity(cityToDelete._id);
    if (!result.error) {
      showToast("Ville supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setCityToDelete(null);
  };

  const { data: cities } = useFetchCitiesQuery({
    page,
    perPage,
    displayName: debouncedDislayName,
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="flex items-center justify-between">
        <div className="w-1/4">
          <TextField
            id="lastName"
            value={displayName}
            onChange={(e) =>
              setDisplayName((e.target as HTMLInputElement).value)
            }
            placeholder="Nom de la ville"
          />
        </div>
        <NavigationLinkBtn
          to="/cities/create"
          label="Créer une ville"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-5/12" />
          <col className="w-5/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th
              key={0}
              className={thClasses}
            >
              Nom
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Active
            </th>
            <th
              key={2}
              className={thClasses}
            >
              Modifier
            </th>
            <th
              key={3}
              className={thClasses}
            >
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {cities?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.displayName} </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <ToggleSwitch
                      isOn={row.isActive}
                      onToggle={() => {
                        toggleActive({
                          cityId: row._id,
                          isActive: !row.isActive,
                        });
                      }}
                    />
                  </div>
                </td>
                <td className={tdClasses}>
                  <Link
                    className="w-full flex justify-center"
                    to={row._id}
                  >
                    <FaEdit className="w-8 text-primary" />{" "}
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: cities?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteCity}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer {cityToDelete?.displayName} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Cities;
